<template>
  <b-row>
    <b-col cols="12" class="mb-4">
      <PageTitle title="Lista de clientes" />
    </b-col>
    <b-col cols="12" class="mb-4">
      <label for="">Lojistas</label>
      <SelectShopkeeperWithApp
        v-model="filters.userIds"
        propValueName="userId"
        @update:value="searchTimeOut()"
      />
    </b-col>
    <b-col cols="12" class="mb-4">
      <label for="">Nome do cliente</label>
      <input
        type="text"
        class="form-control"
        placeholder="Ex: João Silva"
        v-model="filters.name"
        @keydown.enter="get()"
        @keyup="searchTimeOut()"
      />
    </b-col>
    <b-col cols="12">
      <label>Tipos de clientes</label>
      <div>
        <h4>
          <b-badge
            :variant="
              filters.userTypes.includes(USER_TYPES.CLIENT) || isSelectClient
                ? 'primary'
                : 'light'
            "
            class="pointer"
            @click="addUserType(USER_TYPES.CLIENT)"
          >
            Cliente
          </b-badge>
          <b-badge
            :variant="
              filters.userTypes.includes(USER_TYPES.PROFESSIONAL)
                ? 'primary'
                : 'light'
            "
            class="mx-3 pointer"
            @click="addUserType(USER_TYPES.PROFESSIONAL)"
          >
            Profissional
          </b-badge>
          <b-badge
            :variant="
              filters.userTypes.includes(USER_TYPES.SHOPKEEPER)
                ? 'primary'
                : 'light'
            "
            class="mx-3 pointer"
            @click="addUserType(USER_TYPES.SHOPKEEPER)"
          >
            Empresas
          </b-badge>
          <b-badge
            :variant="
              filters.userTypes.includes(USER_TYPES.CLIENT) &&
              filters.userTypes.includes(USER_TYPES.PROFESSIONAL) &&
              filters.userTypes.includes(USER_TYPES.SHOPKEEPER)
                ? 'primary'
                : 'light'
            "
            class="pointer"
            @click="addAllTypes()"
          >
            Todos
          </b-badge>
        </h4>
      </div>
    </b-col>
    <b-col cols="12" class="mt-4">
      <b-button variant="success" @click="get()" :disabled="loading">
        <i class="fas fa-search mr-2"></i>
        Pesquisar
      </b-button>
      <b-button variant="warning" class="ml-2" @click="cleanFilters()">
        <i class="fas fa-trash mr-2"></i>
        Limpar
      </b-button>
    </b-col>
    <b-col
      cols="12"
      class="mt-5 mb-2 d-flex align-items-center justify-content-between"
    >
      <b-row>
        <b-col>
          <QuantityCard
            @isSelectClient="updateSelectClient()"
            icon="fas fa-user"
            text="Clientes"
            color="primary"
            class="cardPointer"
            :quantity="totalClients"
            :type="1"
            :loading="loading"
          />
        </b-col>
        <b-col>
          <QuantityCard
            @isSelectProfessional="updateSelectProfessional()"
            icon="fas fa-toolbox"
            text="Profissionais"
            color="danger"
            class="cardPointer"
            :quantity="totalProfessionals"
            :type="2"
            :loading="loading"
          />
        </b-col>
        <b-col>
          <QuantityCard
            @isSelectCompanye="updateSelectCompanye()"
            icon="fas fa-toolbox"
            text="Empresas"
            color="warning"
            class="cardPointer"
            :quantity="totalCompanies"
            :type="3"
            :loading="loading"
          />
        </b-col>
        <b-col>
          <QuantityCard
            icon="fas fa-toolbox"
            text="Total"
            color="success"
            :quantity="pagination.total || 0"
            :loading="loading"
          />
        </b-col>
      </b-row>
    </b-col>
    <b-col
      cols="12"
      class="mt-5 mb-2 d-flex align-items-center justify-content-between"
    >
      <h3 class="title font-weight-bold">Clientes encontrados</h3>
      <b-button
        v-if="!generatingSpreadsheet"
        pill
        variant="success"
        @click="generateSpreadsheet()"
      >
        <i class="fas fa-file-download mr-2"></i>
        Baixar planilha
      </b-button>
      <b-spinner v-else variant="success" label="Spinning"></b-spinner>
    </b-col>
    <b-col cols="12">
      <b-table
        striped
        hover
        :fields="table.headers"
        :items="table.records"
        :busy="loading"
      >
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle mr-2"></b-spinner>
            <strong>Carregando...</strong>
          </div>
        </template>
        <template #cell(type)="data">
          <span>{{ getUserTypeString(data.item.type) }}</span>
        </template>
      </b-table>
    </b-col>
    <b-col cols="12" class="d-flex align-items-center justify-content-center">
      <b-pagination
        v-model="pagination.currentPage"
        :total-rows="pagination.total"
        :per-page="pagination.itemsPerPage"
        :disabled="loading"
      ></b-pagination>
    </b-col>
  </b-row>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import * as XLSX from 'xlsx';
import USER_TYPES from 'Enums/UserTypes.js';
import makeToast from 'Mixins/makeToast';

import PageTitle from 'Components/PageTitle.vue';

import SelectShopkeeperWithApp from 'Components/Inputs/SelectShopkeeperWithApp.vue';
import QuantityCard from 'Components/Cards/QuantityCard.vue';

export default {
  name: 'ClientList',
  mixins: [makeToast],
  components: {
    PageTitle,
    SelectShopkeeperWithApp,
    QuantityCard,
  },
  data() {
    return {
      USER_TYPES,
      loading: false,
      isSelectClient: false,
      isSelectProfessional: false,
      isSelectCompanye: false,
      generatingSpreadsheet: false,
      table: {
        headers: [
          {
            key: 'index',
            thClass: 'd-none',
            tdClass: 'd-none',
          },
          {
            key: 'clientId',
            thClass: 'd-none',
            tdClass: 'd-none',
          },
          {
            key: 'name',
            label: 'Nome completo',
            sortable: true,
          },
          {
            key: 'email',
            label: 'E-mail',
            sortable: false,
          },
          {
            key: 'cep',
            label: 'CEP',
            sortable: false,
          },
          {
            key: 'address',
            label: 'Endereço',
            sortable: false,
          },
          {
            key: 'telephone',
            label: 'Telefone',
            sortable: false,
          },
          {
            key: 'type',
            label: 'Tipo de cliente',
            sortable: true,
          },
          {
            key: 'createdAt',
            label: 'Cadastrou-se em',
            sortable: true,
          },
        ],
        records: [],
      },
      pagination: {
        currentPage: 1,
        itemsPerPage: 1,
        total: 0,
      },
      filters: {
        name: '',
        userTypes: [],
        userIds: [],
      },
      timer: null,
    };
  },
  computed: {
    ...mapState(['dadosUsuario']),
    totalClients() {
      return this.table.records.filter(x => x.type === USER_TYPES.CLIENT).length;
    },
    totalProfessionals() {
      return this.table.records.filter(x => x.type === USER_TYPES.PROFESSIONAL).length;
    },
    totalCompanies() {
      return this.table.records.filter(x => x.type === USER_TYPES.SHOPKEEPER).length;
    },
  },
  watch: {
    'pagination.currentPage': function () {
      this.get();
    },
  },
  mounted() {
    this.get();
  },
  methods: {
    ...mapActions('shopkeeper', ['getGeneralClients']),
    searchTimeOut() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }

      this.timer = setTimeout(() => {
        this.get();
      }, 800);
    },

    updateSelectClient() {
      this.isSelectClient = !this.isSelectClient;
      this.addUserType(USER_TYPES.CLIENT);
    },

    updateSelectProfessional() {
      this.isSelectProfessional = !this.isSelectProfessional;
      this.addUserType(USER_TYPES.PROFESSIONAL);
    },
    updateSelectCompanye() {
      this.isSelectCompanye = !this.isSelectCompanye;
      this.addUserType(USER_TYPES.SHOPKEEPER);
    },

    addUserType(userType) {
      if (this.filters.userTypes.includes(userType)) {
        const index = this.filters.userTypes.indexOf(userType);
        this.filters.userTypes.splice(index, 1);
        this.get();
        return;
      }

      this.filters.userTypes.push(userType);
      this.get();
    },
    addAllTypes() {
      const validUserTypes = [USER_TYPES.CLIENT, USER_TYPES.PROFESSIONAL, USER_TYPES.SHOPKEEPER];

      validUserTypes.forEach((value) => {
        if (this.filters.userTypes.includes(value)) return;

        this.filters.userTypes.push(value);
      });

      this.get();
    },
    get() {
      this.loading = true;

      const request = {
        // userId: this.dadosUsuario.id,
        currentPage: this.pagination.currentPage,
        ...this.filters,
      };

      this.getGeneralClients(request)
        .then((response) => {
          const { perPage, total, data } = response.data.data || {};

          this.pagination.itemsPerPage = perPage || 15;
          this.pagination.total = total || 0;

          this.table.records = data || [];
        })
        .catch(() => {
          this.makeToast(
            'Erro',
            'Houve um erro ao trazer os clientes',
            'danger'
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
    generateSpreadsheet() {
      this.generatingSpreadsheet = true;

      const clientsData = XLSX.utils.json_to_sheet(
        this.convertColumnsNames(this.table.records)
      );

      const excelFile = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(excelFile, clientsData, 'Lista de clientes');

      const currentDate = new Date().toLocaleDateString('pt-BR');
      const fileName = `Lista de clientes - ${currentDate}`;

      setTimeout(() => {
        this.generatingSpreadsheet = false;
      }, 1000);

      XLSX.writeFile(excelFile, `${fileName}.xlsx`);
    },
    /**
     * @param {Array} records
     * @returns {Array}
     */
    convertColumnsNames(records) {
      if (!records || !records.length) return [];

      const returnArray = [];

      records.forEach((record) => {
        const cell = {};

        Object.keys(record).forEach((key) => {
          const tableColumn = this.table.headers.find((x) => x.key === key);

          cell[`${tableColumn.label}`] = record[key];
        });

        returnArray.push(cell);
      });

      return returnArray;
    },
    cleanFilters() {
      this.filters.name = '';
      this.filters.userTypes = [];
      this.isSelectClient = false;
      this.isSelectProfessional = false;
      this.isSelectCompanye = false;
    },
    getUserTypeString(userType) {
      const strings = {
        [USER_TYPES.CLIENT]: 'Cliente',
        [USER_TYPES.PROFESSIONAL]: 'Profissional',
        [USER_TYPES.SHOPKEEPER]: 'Empresas',
      };

      return strings[userType] || 'Tipo não encontrado';
    },
  },
};
</script>
<style scoped>
.cardPointer {
  cursor: pointer;
}
</style>